import type { ReactNode } from "react";
import type { PhoneBack } from "../../../shared/phone/PhoneNav";
import { Icon, type IconName } from "../../components/ui/Icon";
import { failing, shownState, type Connection, type ConnectionKind } from "../../lib/connectionsApi";
import { CX } from "./CxRows";

/** What the native Connections screens open: a product's connection, or confirming sales for partners. */
export type CxPlace = ConnectionKind | "sales";

/** Each connection as the phone names it, with what it is for under its name. */
export const PLACES: Record<CxPlace, { icon: IconName; name: string; for: string }> = {
  meta: { icon: CX.meta.icon, name: "Meta ad account", for: CX.meta.for },
  email: { icon: CX.email.icon, name: "Email sending", for: CX.email.for },
  sales: { icon: "card", name: "Confirm sales for partners", for: "So the partner who drove a sale gets their reward" },
};

export type Tone = "good" | "warn" | "dim" | "acc";

/** A connection's state in words and colour: the value on its row and the line under its name. */
export function stateOf(c: Connection | undefined, working = false): { words: string; tone: Tone; icon: IconName } {
  const st = working ? "checking" : shownState(c);
  if (st === "ok") return { words: "Verified", tone: "good", icon: "shield" };
  if (st === "warming") return { words: "Warming up", tone: "acc", icon: "clock" };
  if (st === "checking") return { words: "Checking", tone: "dim", icon: "clock" };
  if (st === "fix") {
    const n = Math.max(1, failing(c?.checks ?? []).length);
    return { words: `${n} ${n === 1 ? "thing" : "things"} to fix`, tone: "warn", icon: "flag" };
  }
  return { words: "Not connected", tone: "dim", icon: "plug" };
}

export const Spin = () => <span className="mo-spin" aria-hidden="true" />;

/** The top of a connection's screen: its mark, its name, its state and what it is for. */
export function CxHead({ place, tone, state, stateIcon, line, on }: { place: CxPlace; tone: Tone; state: string; stateIcon: IconName; line: ReactNode; on: boolean }) {
  const p = PLACES[place];
  return (
    <header className="mo-chead">
      <span className={`pn-ico ${place === "sales" ? "violet" : on ? "" : "plain"}`}>
        <Icon name={p.icon} />
      </span>
      <h1>{p.name}</h1>
      <span className={`mo-st ${tone}`}>
        <Icon name={stateIcon} />
        {state}
      </span>
      <p>{line}</p>
    </header>
  );
}

/** A check's mark: passed, failed, or still to come. */
export function CheckMark({ kind }: { kind: "ok" | "bad" | "pend" }) {
  return (
    <span className={`mo-ck ${kind}`}>
      <Icon name={kind === "ok" ? "check" : kind === "bad" ? "x" : "clock"} />
    </span>
  );
}

/** Most letters the bar holds for the back label and the title side by side, as the phone shortens to "Back". */
const BAR_ROOM = 22;

/** The back button once the title has come into the bar: "Back" when both would not fit. */
export function barBack(back: PhoneBack | undefined, titleShown: boolean, title: string): PhoneBack | undefined {
  if (!back || !titleShown || (back.label ?? "").length + title.length <= BAR_ROOM) return back;
  return { ...back, label: undefined };
}
